import { useCallback, useEffect, useRef } from 'react';
import { FiX } from 'react-icons/fi';
import { useDarkMode } from '../../context/DarkModeContext';

export default function BottomSheet({
  open,
  onClose,
  title,
  children,
  height = '70vh',
  showHandle = true,
  showClose = true,
}) {
  const { COL } = useDarkMode();
  const sheetRef = useRef(null);
  const startY = useRef(null);
  const currentY = useRef(null);
  const dragging = useRef(false);

  const handleClose = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && open) handleClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, handleClose]);

  const onTouchStart = (e) => {
    if (e.target.closest('[data-no-drag]')) return;
    startY.current = e.touches[0].clientY;
    dragging.current = true;
  };

  const onTouchMove = (e) => {
    if (!dragging.current || !sheetRef.current) return;
    currentY.current = e.touches[0].clientY;
    const delta = currentY.current - startY.current;
    if (delta > 0) {
      sheetRef.current.style.transform = `translateY(${delta}px)`;
      sheetRef.current.style.transition = 'none';
    }
  };

  const onTouchEnd = () => {
    if (!dragging.current || !sheetRef.current) return;
    dragging.current = false;
    if (currentY.current && startY.current) {
      const delta = currentY.current - startY.current;
      if (delta > 100) {
        handleClose();
      } else {
        sheetRef.current.style.transform = '';
        sheetRef.current.style.transition = 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)';
      }
    }
    startY.current = null;
    currentY.current = null;
  };

  if (!open) return null;

  const overlayBg = COL.overlay || 'rgba(11,26,74,0.5)';
  const sheetBg = COL.surface || '#FFFFFF';
  const titleColor = COL.pri || '#0B1A4A';
  const handleBg = COL.faint || '#D1D5DB';
  const closeBg = COL.bg || '#F5F7FA';
  const closeColor = COL.muted || '#6B7280';

  return (
    <>
      <div
        onClick={handleClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1200,
          background: overlayBg,
          backdropFilter: 'blur(4px)',
          animation: 'fadeIn 0.2s ease',
          WebkitTapHighlightColor: 'transparent',
        }}
      />
      <div
        ref={sheetRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1201,
          maxHeight: height,
          height: height,
          background: sheetBg,
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.15)',
          animation: 'slideUp 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
          transition: 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
          overflow: 'hidden',
        }}
      >
        {showHandle && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '8px 0 4px',
              flexShrink: 0,
            }}
          >
            <div
              style={{ width: '36px', height: '4px', borderRadius: '2px', background: handleBg }}
            />
          </div>
        )}
        {(title || showClose) && (
          <div
            data-no-drag
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '6px 16px 10px',
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: '16px', fontWeight: 700, color: titleColor }}>
              {title || ''}
            </span>
            {showClose && (
              <button
                onClick={handleClose}
                data-no-drag
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  border: 'none',
                  background: closeBg,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: closeColor,
                  fontSize: '18px',
                }}
              >
                <FiX size={18} />
              </button>
            )}
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {children}
        </div>
      </div>
    </>
  );
}
